"use client";

import { useCart, useSelection } from "@trydig/centra/client";
import { Slot } from "@trydig/ui";
import { useUrlSearchParams } from "@trydig/ui/next";
import { type ComponentProps, Suspense, useEffect } from "react";
import { Button } from "@/components/ui/Button";
import {
	DrawerClose,
	DrawerContent,
	DrawerFooter,
	DrawerHeader,
	DrawerMain,
	DrawerRoot,
	DrawerTitle,
	DrawerViewport,
} from "@/components/ui/Drawer";
import { CartItems } from "@/features/cart/components/CartItems";
import { CartSummary } from "@/features/cart/components/CartSummary";
import { useTranslations } from "@/i18n/client";
import { gtm } from "@/lib/gtm";
import { selectionItemsToGTMProducts } from "@/lib/gtm/utils";
import { EmptyCart } from "../EmptyCart";
import { UpsellProducts } from "../UpsellProducts";
import styles from "./CartModal.module.css";

export const useCartModal = () => {
	const searchParams = useUrlSearchParams();

	const open = searchParams.get("cart") === "open";

	const setOpen = (open: boolean) => {
		if (open) {
			searchParams.set("cart", "open");
		} else {
			searchParams.delete("cart");
		}
	};

	return [open, setOpen] as const;
};

export const CartModal = () => {
	const { data: cartData } = useCart();
	const { data } = useSelection();
	const [open, setOpen] = useCartModal();
	const t = useTranslations();

	useEffect(() => {
		if (open && cartData?.items && cartData?.currency) {
			const items = selectionItemsToGTMProducts({
				items: cartData.items,
				currency: cartData.currency,
			});

			gtm.viewCart({
				items,
				currency: cartData.currency,
				value: items?.reduce((a, b) => (a + b.price) * (b.quantity || 1), 0),
			});
		}
	}, [open, cartData?.currency, cartData?.items]);

	return (
		<DrawerRoot open={open} onOpenChange={setOpen}>
			<DrawerViewport>
				<DrawerContent className={styles.content}>
					<DrawerHeader inline>
						<DrawerTitle small>{t("Cart")}</DrawerTitle>
						<DrawerClose inline />
					</DrawerHeader>
					<DrawerMain className={styles.main}>
						{(cartData?.itemCount ?? 0) > 0 ? (
							<>
								<CartItems />
								<div className={styles.cartDetails}>
									<UpsellProducts />
									<CartSummary />
								</div>
							</>
						) : (
							<EmptyCart />
						)}
					</DrawerMain>
					{(cartData?.itemCount ?? 0) > 0 && (
						<DrawerFooter className={styles.footer}>
							<Button
								data-testid="go-to-checkout-button"
								className={styles.checkoutBtn}
								href="/checkout"
							>
								{t("Go to checkout")}
							</Button>
							<button
								type="button"
								className={styles.continueShoppingBtn}
								onClick={() => {
									setOpen(false);
								}}
							>
								{t("Continue shopping")}
							</button>
						</DrawerFooter>
					)}
				</DrawerContent>
			</DrawerViewport>
		</DrawerRoot>
	);
};

const UnsuspendedCartModalTrigger = ({
	asChild,
	...props
}: ComponentProps<"button"> & {
	asChild?: boolean;
}) => {
	const [, setOpen] = useCartModal();
	const Component = asChild ? Slot : "button";

	return <Component {...props} onClick={() => setOpen(true)} />;
};

export const CartModalTrigger: typeof UnsuspendedCartModalTrigger = (props) => {
	return (
		<Suspense fallback={props.asChild ? props.children : null}>
			<UnsuspendedCartModalTrigger {...props} />
		</Suspense>
	);
};
